"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NavLink = NavLink;
exports.getNavLinkStyles = getNavLinkStyles;
const jsx_runtime_1 = require("@emotion/react/jsx-runtime");
const react_1 = require("react");
const react_aria_1 = require("react-aria");
const components_1 = require("./");
const Css_1 = require("../Css");
const useGetRef_1 = require("../hooks/useGetRef");
const getInteractiveElement_1 = require("../utils/getInteractiveElement");
const Icon_1 = require("./Icon");
function NavLink(props) {
    const { disabled: isDisabled, label, openInNew, contrast = false, menuTriggerProps, buttonRef, ...otherProps } = props;
    const ariaProps = { children: label, isDisabled, ...menuTriggerProps, ...otherProps };
    const { href, active = false, icon = false, variant } = ariaProps;
    const ref = (0, useGetRef_1.useGetRef)(buttonRef);
    const { buttonProps, isPressed } = (0, react_aria_1.useButton)({ ...ariaProps, elementType: href ? "a" : "button" }, ref);
    const { hoverProps, isHovered } = (0, react_aria_1.useHover)({ isDisabled });
    const { isFocusVisible, focusProps } = (0, react_aria_1.useFocusRing)(ariaProps);
    const { baseStyles, activeStyles, focusRingStyles, hoverStyles, disabledStyles, pressedStyles } = (0, react_1.useMemo)(() => getNavLinkStyles(variant, contrast), [variant, contrast]);
    const linkAttributes = {
        className: components_1.navLink,
        ref: ref,
        /** does not focus if disabled */
        tabIndex: isDisabled ? -1 : 0,
        /** aria-current represents the current page within a set of pages */
        "aria-current": active ? "page" : undefined,
        css: {
            ...baseStyles,
            ...(active && activeStyles),
            ...(isDisabled && disabledStyles),
            ...(isFocusVisible && focusRingStyles),
            ...(isHovered && hoverStyles),
            ...(isPressed && pressedStyles),
        },
    };
    const linkContent = ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [label, icon && ((0, jsx_runtime_1.jsx)("span", { css: Css_1.Css.ml1.$, children: (0, jsx_runtime_1.jsx)(Icon_1.Icon, { icon: icon }) }))] }));
    return (0, getInteractiveElement_1.getButtonOrLink)(linkContent, href, { ...(0, react_aria_1.mergeProps)(buttonProps, focusProps, hoverProps), ...linkAttributes });
}
function getNavLinkStyles(variant, contrast) {
    return navLinkVariantStyles(contrast)[variant];
}
const baseStyles = Css_1.Css.df.aic.hPx(32).pyPx(6).px1.br4.smMd.outline0.$;
const navLinkVariantStyles = (contrast) => ({
    side: {
        baseStyles: { ...baseStyles, ...Css_1.Css.gray700.if(contrast).gray600.$ },
        activeStyles: Css_1.Css.blue700.bgBlue50.if(contrast).white.bgGray700.$,
        disabledStyles: Css_1.Css.gray400.cursorNotAllowed.if(contrast).gray800.$,
        focusRingStyles: Css_1.Css.bgBlue50.bshFocus.if(contrast).bgGray700.white.$,
        hoverStyles: Css_1.Css.gray700.bgGray100.if(contrast).bgGray800.gray600.$,
        pressedStyles: Css_1.Css.gray700.bgGray200.if(contrast).bgGray200.gray800.$,
    },
    global: {
        baseStyles: { ...baseStyles, ...Css_1.Css.add("width", "max-content").gray500.$ },
        activeStyles: Css_1.Css.white.bgGray900.$,
        disabledStyles: Css_1.Css.gray400.cursorNotAllowed.$,
        focusRingStyles: Css_1.Css.gray500.bgGray900.add("boxShadow", `0px 1px 2px rgba(0, 0, 0, 0.05), 0px 0px 0px 2px #242424, 0px 0px 0px 4px #0EA5E9`).$,
        hoverStyles: Css_1.Css.gray500.bgGray900.$,
        pressedStyles: Css_1.Css.gray500.bgGray700.$,
    },
});
